import React, { useRef, useLayoutEffect, useState, useCallback } from "react";

const measureTextWidth = (text, font) => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  context.font = font;
  return context.measureText(text).width;
};

function ShrinkToFitText({
  text,
  style = {},
  className = "",
  onClick,
  title,
  ...props
}) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [adjustedFontSize, setAdjustedFontSize] = useState(null);

  const measureAndFit = useCallback(() => {
    if (!textRef.current || !containerRef.current) return;

    const cs = window.getComputedStyle(containerRef.current);
    const padL = parseFloat(cs.paddingLeft) || 0;
    const padR = parseFloat(cs.paddingRight) || 0;
    const availableWidth = containerRef.current.clientWidth - padL - padR;

    if (availableWidth <= 0) return;

    // Get the original font size from the style (backend-defined)
    const originalFontSize = parseFloat(style.fontSize) || 15;
    const textStyle = window.getComputedStyle(textRef.current);
    const fontProps = `${textStyle.fontStyle} ${textStyle.fontWeight}`;
    const fontFamily = textStyle.fontFamily;

    // Measure at original font size
    const font = `${fontProps} ${originalFontSize}px ${fontFamily}`;
    const textWidth = measureTextWidth(text || "", font);

    if (textWidth > availableWidth) {
      // Reduce font size proportionally to fit
      const ratio = availableWidth / textWidth;
      const newSize = Math.max(6, originalFontSize * ratio);
      setAdjustedFontSize(newSize);
    } else {
      setAdjustedFontSize(null);
    }
  }, [text, style.fontSize]);

  useLayoutEffect(() => {
    measureAndFit();
    const handleResize = () => measureAndFit();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [measureAndFit]);

  return (
    <div
      ref={containerRef}
      className={className}
      onClick={onClick}
      title={title}
      style={{
        ...style,
        overflow: "hidden",
        display: "flex",
        alignItems: style.alignItems || "flex-end",
        justifyContent: style.justifyContent || "flex-start",
      }}
      {...props}
    >
      <span
        ref={textRef}
        style={{
          display: "block",
          whiteSpace: "nowrap",
          fontSize: adjustedFontSize ? `${adjustedFontSize}px` : undefined,
        }}
      >
        {text}
      </span>
    </div>
  );
}

export default ShrinkToFitText;
